import { Card } from "../../components/Card";
import { LeaderBoard } from "./components/LeaderBoard";
import { TeamInsights } from "./components/TeamInsights";
import { TrendIndicator } from "../../components/TrendIndicator";
import { SelectInput } from "../../components/SelectInput/SelectInput";
import { ReactComponent as CalendarIcon } from "../../assets/icons/calendar.svg";
import {
  isAllTimeDateRangeEnabled,
  isLeaderboardLabelEnabled,
} from "../../integrations/posthog/featureFlags";
import { DATE_RANGE_OPTIONS, WINDOWED_RANGES } from "./utils/constants";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../services/api";
import { useApp } from "../../services/app-context";
import { useUser } from "../../services/user-context";
import { isAdmin } from "../../services/utils";
import { Rating } from "../praxis-call/components/Rating";
import { LottieLoading } from "../../components/Loading/LottieLoading";
import { NotificationButton } from "../../components/NotificationButton";

const levels = {
  0: "Account Specialist",
  1: "Manager",
  2: "Zone Manager",
  3: "Director",
}

function AnalyticsCard({
  title,
  value,
  percentage,
  showStar = false,
  improvement,
  loading,
}: {
  title: string;
  value: string;
  percentage?: number;
  showStar?: boolean;
  improvement?: {
    text: string;
    trend: string;
  };
  loading?: boolean;
}) {
  return (
    <Card className="flex flex-col gap-4 !p-4 lg:min-w-[250px] flex-1">
      <div className="text-[19px] text-text-primary">{title}</div>

      {loading ? <LottieLoading className="!w-full !h-[80px]" /> : (
        <>
          <div className="flex gap-2 items-center">
            {showStar && <Rating contentClassName="!ml-0" value={Number(value)} showMax={false} showCount={false} />}
            <div className="text-gray text-[20px] leading-[24px] font-medium">
              {/* TODO: use avg score */}
              {value}
            </div>
          </div>
          {improvement ? (
            <div className="mt-1 mb-5">
              <TrendIndicator
                trendInfo={improvement ?? {}}
                iconSize={14}
                rotateIconWhenDown={false}
                positiveTrends={["up", "equal"]}
                textClassName="text-[#969696] text-xs"
                suffixFallback="from last week"
              />
            </div>
          ) : null}
        </> 
      )}
    </Card>
  );
}

const buildVariable = (metrics: string, dateRange: string) => {
  return `${metrics}${dateRange || ""}`;
};

/** The picker the flag replaces: one button per window. Goes away with the flag. */
function DateRangeButtons({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex gap-2">
      {WINDOWED_RANGES.map((range) => (
        <button
          key={range.value}
          className={`flex items-center gap-2 text-base  py-[6px] px-3 text-text-primary rounded-[5px] ${value === range.value
            ? "shadow-[0px_2px_7.8px_0px_#00000021] border border-[#B7B7B7] bg-background4"
            : "bg-white hover:bg-white/60"
            }`}
          onClick={() => onChange(range.value)}
        >
          <CalendarIcon />
          {range.label}
        </button>
      ))}
    </div>
  );
}

export function ManagerDashboard() {
  const { tenantProfile } = useApp();
  const { userInfo } = useUser() as any;
  const role = userInfo?.role;
  const [selectedUserID, setSelectedUserID] = useState<string>("");

  const [selectedDateRange, setSelectedDateRange] =
    useState<string>("7Days");
  const allTimeRangeEnabled = isAllTimeDateRangeEnabled();
  const leaderboardLabelEnabled = isLeaderboardLabelEnabled();

  const leaderboardOptions: string[] = useMemo(() => {
    if (!tenantProfile) return [];
    const adminOptions = tenantProfile?.metrics?.leaderboard?.adminOptions;
    if (isAdmin(role) && adminOptions) return adminOptions;
    return tenantProfile?.metrics?.leaderboard?.options ?? ["scores"];
  }, [tenantProfile, role]);
  const [leaderboardMode, setLeaderboardMode] = useState<string>("");

  useEffect(() => {
    if (leaderboardOptions.length > 0 && !leaderboardOptions.includes(leaderboardMode)) {
      setLeaderboardMode(leaderboardOptions[0]);
    }
  }, [leaderboardOptions]);

  const {
    data: metricsData,
    isLoading: isLoadingMetrics,
  } = useQuery({
    queryKey: ["team-metrics", selectedUserID],
    queryFn: () => api.getTeamMetrics({ userID: selectedUserID }),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  const {
    data: leaderBoardData,
    isLoading: isLoadingLeaderBoard,
    isFetching: isFetchingLeaderBoard,
  } = useQuery({
    queryKey: ["team-leaderboard", selectedUserID, selectedDateRange, leaderboardMode],
    queryFn: () => api.getTeamLeaderBoard({
      level: "",
      userID: selectedUserID,
      dateRange: selectedDateRange.replace("Days", ""),
      rankBy: leaderboardMode,
    }),
    enabled: !!leaderboardMode,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  const handleLeaderBoardSelect = (userID: string) => {
    setSelectedUserID(userID);
  };

  const handleResetFilters = () => {
    setSelectedUserID("");
  };

  const metrics = metricsData?.data;

  const reportLevelKey = Math.max(0, Number(metrics?.userLevel ?? 0) - 1);
  const reportLevelLabel = (levels[reportLevelKey] ?? levels[0]).toLowerCase() + "s";

  const isLoading = isLoadingMetrics || (isLoadingLeaderBoard && isFetchingLeaderBoard);

  return (
    <div className="p-6 flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <div className="flex-col justify-start items-start tall2:gap-2 gap-2 md:gap-4 inline-flex">
          <div className="text-text-primary text-2xl font-bold">
            Team Dashboard
          </div>
          <div className="text-text-primary text-sm md:text-lg font-normal">
            View all your {reportLevelLabel} in your region and their performance
          </div>
        </div>

        <NotificationButton />
      </div>
      <>
        <div className="flex gap-2 justify-between items-center">
          {allTimeRangeEnabled ? (
            <SelectInput
              containerClassName="w-[180px]"
              options={DATE_RANGE_OPTIONS}
              value={selectedDateRange}
              onChange={(e) => setSelectedDateRange(e.target.value)}
              className="bg-white text-base text-text-primary rounded-[5px] py-[6px] pl-3"
            />
          ) : (
            <DateRangeButtons value={selectedDateRange} onChange={setSelectedDateRange} />
          )}
          <div className="flex gap-8">
            {selectedUserID ?
              <div className="flex gap-2">
                <button
                  onClick={handleResetFilters}
                  className="flex items-center gap-2 text-base  py-[6px] px-3 text-text-primary rounded-[5px] hover:bg-white/60 bg-white"
                >
                  Reset
                </button>
              </div> : null}
          </div>
        </div>
        <div>
        </div>
        <div className="flex items-stretch gap-4">
          <AnalyticsCard
            title="Avg. Session Score"
            value={(Math.round((metrics?.[buildVariable("avgPraxisScore", selectedDateRange)] ?? 0) * 100) / 100).toString()}
            // percentage={2}
            showStar
            improvement={metrics?.[buildVariable("avgPraxisScoreImprovement", selectedDateRange)]}
            loading={isLoading}
          />
          <AnalyticsCard
            title="# of Sessions"
            value={`${metrics?.[buildVariable("avgPraxisSessionsCount", selectedDateRange)] || 0} Sessions`}
            // percentage={!metrics?.[buildVariable("avgPraxisSessionsCount", selectedDateRange)] ? 0 : 2}
            improvement={metrics?.[buildVariable("avgPraxisSessionsCounImprovement", selectedDateRange)]}
            loading={isLoading}
          />
          <AnalyticsCard
            title="Time Per Session"
            value={metrics?.[buildVariable("avgTimePerSession", selectedDateRange) + "HRF"] ?? "-"}
            improvement={metrics?.[buildVariable("avgTimePerSessionImprovement", selectedDateRange)]}
            loading={isLoading}
          />
        </div>
        <div className="flex items-stretch gap-4">
          <div className="h-auto">
            <LeaderBoard
              loading={isLoadingLeaderBoard || !leaderboardMode}
              title="Team Leaderboard"
              data={leaderBoardData?.data || []}
              onSelect={handleLeaderBoardSelect}
              selectedUserID={selectedUserID}
              allowDrilldown={Number(metrics?.userLevel) > 1}
              drilldownTooltip={tenantProfile?.info_badges?.team_leaderboard}
              mode={leaderboardMode}
              availableModes={leaderboardOptions}
              onModeChange={setLeaderboardMode}
              showLabel={leaderboardLabelEnabled}
            />
          </div>
          <div className="flex-1">
            <TeamInsights loading={isLoadingMetrics} data={metrics?.[buildVariable("insights", selectedDateRange)] || metrics?.insights || []} />
          </div>
        </div>
      </>
    </div>
  );
}
